import { useState } from "react";
import clsx from "clsx";
import { Markdown } from "@/ui/atoms/Markdown/Markdown";

interface ExpandableMarkdownProps {
    text: string;
    maxLength?: number;
    className?: string;
}

/**
 * Preprocesses text to add line breaks before bold section headers
 * for better readability in markdown rendering
 */
const formatTextForMarkdown = (text: string): string => {
    if (!text) return text;

    // Add double newlines before bold section headers (pattern: **Text** –)
    // This helps create visual separation between sections
    return text.replace(/(\*\*[^*]+\*\*\s*–)/g, "\n\n$1");
};

export const ExpandableMarkdown = ({
    text,
    maxLength = 500,
    className,
}: ExpandableMarkdownProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) {
        return <span className={clsx(className, "body-s subdued")}>-</span>;
    }

    const formattedText = formatTextForMarkdown(text);
    const shouldTruncate = formattedText.length > maxLength;

    if (!shouldTruncate) {
        return (
            <div className={clsx(className, "body-s subdued")}>
                <Markdown>{formattedText}</Markdown>
            </div>
        );
    }

    const displayText = isExpanded
        ? formattedText
        : `${formattedText.slice(0, maxLength)}...`;

    return (
        <div className={clsx(className, "body-s subdued")}>
            <Markdown>{displayText}</Markdown>
            <span
                role="button"
                onClick={() => setIsExpanded(!isExpanded)}
                tabIndex={0}
                className="text-link ml-1 cursor-pointer"
            >
                {isExpanded ? "Show less" : "See more"}
            </span>
        </div>
    );
};
